import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, Profile, UserRole } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  role: UserRole | null;
  businessId: string | null;
  shopId: string | null;
  isLoading: boolean;
  profileError: string | null;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refetchProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle(); // maybeSingle returns null (not error) when row missing
  if (error) {
    console.error('[AuthContext] fetchProfile error:', error.message, error.code);
    return null;
  }
  return data as Profile | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser]               = useState<User | null>(null);
  const [profile, setProfile]         = useState<Profile | null>(null);
  const [isLoading, setIsLoading]     = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);

  const loadProfile = async (u: User) => {
    setProfileError(null);
    const p = await fetchProfile(u.id);
    if (!p) {
      // Profile row missing — surface a clear error instead of infinite load
      setProfileError(
        `No profile found for this account. ` +
        `Please ask your administrator to create a profile row in the profiles table for user ID: ${u.id}`
      );
    }
    setProfile(p);
  };

  useEffect(() => {
    let mounted = true;

    // onAuthStateChange fires INITIAL_SESSION on mount — use that as the
    // single source of truth so we never race between getSession + the listener.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        if (event === 'SIGNED_OUT' || !session?.user) {
          setUser(null);
          setProfile(null);
          setProfileError(null);
          setIsLoading(false);
          return;
        }

        // INITIAL_SESSION, SIGNED_IN, TOKEN_REFRESHED all have a user
        if (session?.user) {
          setUser(session.user);
          await loadProfile(session.user);
          if (mounted) setIsLoading(false);
          return;
        }

        // Catch-all — should never reach here, but ensures spinner always stops
        if (mounted) setIsLoading(false);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = async (email: string, password: string) => {
    setIsLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setIsLoading(false);
      return { error };
    }
    // onAuthStateChange will handle setting user + profile + isLoading=false
    // But set user immediately so UI doesn't flicker
    if (data.user) setUser(data.user);
    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setProfileError(null);
  };

  const refetchProfile = async () => {
    if (!user) return;
    setProfileError(null);
    const p = await fetchProfile(user.id);
    setProfile(p);
    if (!p) setProfileError('Profile not found. Contact your administrator.');
  };

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      role: profile?.role ?? null,
      businessId: profile?.business_id ?? null,
      shopId: profile?.shop_id ?? null,
      isLoading,
      profileError,
      signIn,
      signOut,
      refetchProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}